import React, { Suspense } from 'react';
import { ResourceType } from '@cotera/api';
import { ChatArtifactAccessGate } from '@cotera/client/v0/chat/artifacts/chat-artifact-access-gate';
import { ActiveArtifactIdOverride } from '@cotera/client/v0/chat/artifact-panel-context';
import { AgentPageWithFetchedData } from '@cotera/client/app/v1/agent/agent.page';
import { DatasetPageWithFetchedData } from '@cotera/client/app/v1/dataset/dataset.page';
import { ArtifactDrillProvider } from './workflow-drill-context';
import { WorkflowDrillDetail } from './workflow-drill-detail';
import type {
  WorkflowAgentViewPayload,
  WorkflowDatasetViewPayload,
  WorkflowDrillViewPayload,
} from './workflow-artifact-views';

const AgentStackSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="flex-1 min-h-0 overflow-y-auto p-5">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
      </div>
    </div>
  </div>
);

/** Node/trigger config body for the in-panel stack (shell chrome lives on the page). */
export const WorkflowDrillStackContent: React.FC<{
  view: WorkflowDrillViewPayload;
  onClose: () => void;
}> = ({ view, onClose }) => (
  <ArtifactDrillProvider target={view.target} onClose={onClose}>
    <WorkflowDrillDetail target={view.target} layout="page" />
  </ArtifactDrillProvider>
);

/** Full agent artifact experience, hosted on the workflow artifact stack. */
export const WorkflowAgentStackContent: React.FC<{
  view: WorkflowAgentViewPayload;
}> = ({ view }) => (
  // Panel tab stays the workflow; override so the agent model is targeted while
  // this stack view is open (Ctrl+S / dirty-state targeting).
  <ActiveArtifactIdOverride artifactId={view.agentId}>
    <ChatArtifactAccessGate
      type={ResourceType.Agent}
      id={view.agentId}
      artifactType="agent"
      fallback={<AgentStackSkeleton />}
    >
      <Suspense fallback={<AgentStackSkeleton />}>
        <AgentPageWithFetchedData id={view.agentId} />
      </Suspense>
    </ChatArtifactAccessGate>
  </ActiveArtifactIdOverride>
);

/**
 * The watched dataset, hosted on the workflow artifact stack.
 *
 * "Editing" a dataset trigger means inspecting the rows it fires on, so the
 * pencil drills into the dataset page rather than a config drawer — the same
 * shape as an agent node drilling into its agent.
 */
export const WorkflowDatasetStackContent: React.FC<{
  view: WorkflowDatasetViewPayload;
}> = ({ view }) => (
  // Panel tab stays the workflow; override so the dataset model is the target
  // while this stack view is open (Ctrl+S / dirty-state targeting).
  <ActiveArtifactIdOverride artifactId={view.datasetId}>
    <ChatArtifactAccessGate
      type={ResourceType.Dataset}
      id={view.datasetId}
      artifactType="dataset"
      fallback={<AgentStackSkeleton />}
    >
      <Suspense fallback={<AgentStackSkeleton />}>
        <DatasetPageWithFetchedData id={view.datasetId} mode="artifact" />
      </Suspense>
    </ChatArtifactAccessGate>
  </ActiveArtifactIdOverride>
);
